import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { AsyncApiService } from '@spike/core';
import { EntityApiEnum } from 'src/app/config/entity-api.enum';
import { Employee } from '../models/employee.model';
import { Machine } from '../models/machine.model';
import { Supplier } from '../models/supplier.model';
import { Operation } from '../models/operation';
import { Client } from '../models/client.model';
import { Equipment } from '../models/equipment.model';
import { Vehicle } from '../models/vehicle.model';

@Injectable({ providedIn: 'root' })
export class OrganizationStoreService {
    
    private readonly asyncApiService = inject(AsyncApiService);
    private readonly entityMap :Map<EntityApiEnum, WritableSignal<any[]>> = new Map<EntityApiEnum, WritableSignal<any[]>>([]);


    public async save(apiEnum: EntityApiEnum, entityKey:string ,entity: any){
        await this.asyncApiService.save(apiEnum, entity, entity[entityKey]);
        await this.reload(apiEnum);
    }

    public async delete(apiEnum: EntityApiEnum, data: any) {
        data.stateId=0;
        await this.asyncApiService.updateById(apiEnum, data);
        await this.reload(apiEnum);
    }

    public async restore(apiEnum: EntityApiEnum, data: any) {
        data.stateId=1;
        await this.asyncApiService.updateById(apiEnum, data);
        await this.reload(apiEnum);
    }

   public getList(apiEnum: EntityApiEnum): Signal<any[]> {
        if (!this.entityMap.has(apiEnum)) {
            this.entityMap.set(apiEnum, signal<any[]>([]));
            this.reload(apiEnum);
        }
        return this.entityMap.get(apiEnum)!.asReadonly();
    }

    private async reload(apiEnum: EntityApiEnum){
        const res = await this.asyncApiService.selectAll<any>(apiEnum);
        if (!this.entityMap.has(apiEnum)) {
            this.entityMap.set(apiEnum, signal(res));
        }else{
            this.entityMap.get(apiEnum)!.set(res);

        }
    }
    
    public getReactiveList(apiEnumSignal: Signal<EntityApiEnum>): Signal<any[]> {
        return computed(() => {
            const api = apiEnumSignal();
            const listSignal = this.getList(api);
            return listSignal();
        });
    }
 

    public findReactive<T>(apiEnum: EntityApiEnum, key: keyof T, id: number | undefined): Signal<T | undefined> {
        return computed(() => {
            if (!id) return undefined;
            const list = this.getList(apiEnum)();
            const found = list.find(x => x[key] === id);

            if (!found && list.length > 0) {
                this.reload(apiEnum);
            }

            return found;
        });
    }

    public getEmployee(id: number | Signal<number>): Signal<Employee | undefined> {
        const idSignal = typeof id === 'function' ? id : signal(id);
        return this.findReactive<Employee>(EntityApiEnum.EMPLOYEE, 'employeeId', idSignal());
    }
        
    public getMachine(id: number): Signal<Machine | undefined>{
        const idSignal = typeof id === 'function' ? id : signal(id);
        return this.findReactive<Machine>(EntityApiEnum.MACHINE, 'machineId', idSignal());
    }

    public getOperation(id: number): Signal<Operation | undefined>{
        const idSignal = typeof id === 'function' ? id : signal(id);
        return this.findReactive<Operation>(EntityApiEnum.OPERATION, 'operationId', idSignal());
    }

    public getSupplier(id: number): Signal<Supplier | undefined>{
        const idSignal = typeof id === 'function' ? id : signal(id);
        return this.findReactive<Supplier>(EntityApiEnum.SUPPLIER, 'supplierId', idSignal());
    }

    public getClient(id: number | Signal<number | undefined>): Signal<Client | undefined> {
        const idSignal = typeof id === 'function' ? id : signal(id);
        return this.findReactive<Client>(EntityApiEnum.CLIENT, 'clientId', idSignal());
    }

    public getEquipment(id: number | Signal<number | undefined>): Signal<Equipment | undefined> {
        const idSignal = typeof id === 'function' ? id : signal(id);
        return this.findReactive<Equipment>(EntityApiEnum.EQUIPMENT, 'equipmentId', idSignal());
    }
    
     public getVehicle(id: number | Signal<number | undefined>): Signal<Vehicle | undefined> {
        const idSignal = typeof id === 'function' ? id : signal(id);
        return this.findReactive<Vehicle>(EntityApiEnum.VEHICLE, 'vehicleId', idSignal());
    }

    public getFilteredList<T>(
        apiEnumSignal: Signal<EntityApiEnum>, 
        filterSignal: Signal<Partial<T> | null | undefined>,
        operationId?: Signal<number | undefined>    
    ): Signal<T[]> {
        return computed(() => {
            const api = apiEnumSignal();
            const filters = filterSignal();
            const list = this.getList(api)() as T[];
            const operationIdValue = operationId ? operationId() : undefined;
            // Se non c'è un filtro, restituisci la lista intera
            let res=[]
            if (!filters || Object.keys(filters).length === 0) {
                res = list;
            }
            else{
                res= list.filter(item => {
                    // Verifica ogni proprietà definita nell'oggetto filtro
                    return Object.entries(filters).every(([key, filterValue]) => {
                        const itemValue = (item as any)[key];

                        // Se il filtro per questa chiave è vuoto (null/undefined/''), lo ignoriamo
                        if (filterValue === null || filterValue === undefined || filterValue === '') {
                            return true;
                        }

                        // Logica di confronto
                        if (typeof filterValue === 'string' && typeof itemValue === 'string') {
                            // Ricerca parziale case-insensitive per le stringhe
                            return itemValue.toLowerCase().includes(filterValue.toLowerCase());
                        }

                        // Confronto esatto per numeri, booleani o altri tipi
                        return itemValue === filterValue;
                    });
                });
            }

            if(apiEnumSignal() === EntityApiEnum.MACHINE && operationIdValue !== undefined && operationIdValue !== null){
                const operation = this.getOperation(operationIdValue)();
                if(operation && operation.machines){
                    const machineMap: Record<number, Machine> = {};
                    operation.machines.forEach(m => {machineMap[m.machineId] = m;});
                    res = res.filter(item => item && machineMap[(item as any).machineId]);
                }
            }
            if(apiEnumSignal() === EntityApiEnum.EMPLOYEE && operationIdValue !== undefined && operationIdValue !== null){
                const operation = this.getOperation(operationIdValue)();
                if(operation && operation.employees){
                    const employeeMap: Record<number, Employee> = {};
                    operation.employees.forEach(e => {employeeMap[e.employeeId] = e;});
                    res = res.filter(item => item && employeeMap[(item as any).employeeId]);
                }
            }
            return res;
        });
    }
}